/**
 * Pure, deterministic helpers for importing expenses from external apps
 * (Splitwise, Dlužníček, ...) via uploaded CSV/Excel exports.
 *
 * The AI step only detects a column mapping from a few sample rows; these
 * helpers apply that mapping to every row without any further AI calls, so the
 * import is reproducible and cheap. Shared between the web app (preview) and the
 * Edge Function so both produce identical results.
 */

import type { SplitType } from '../types';

export type DateFormatHint = 'ISO' | 'YMD' | 'DMY' | 'MDY' | null;

export type SplitStyle = 'delimited' | 'per_member' | 'none';

export interface ImportMemberColumn {
  /** Header of the column that holds this participant's share. */
  columnName: string;
  /** Participant identity printed in the header (name or email). */
  participant: string;
}

/**
 * Column mapping produced by the AI detection step. Flattened (no discriminated
 * union) so it maps cleanly onto an OpenAI strict json_schema.
 */
export interface ImportColumnMapping {
  sourceApp: string | null;
  confidence: number;
  /** Whether the first row is a header. When false, columns are named "Column N". */
  hasHeader: boolean;
  amountColumn: string;
  currencyColumn: string | null;
  /** Used when there is no per-row currency column. */
  fallbackCurrency: string | null;
  descriptionColumn: string | null;
  categoryColumn: string | null;
  dateColumn: string | null;
  dateFormat: DateFormatHint;
  typeColumn: string | null;
  /** Column holding the payer (may be empty when the format encodes it differently). */
  paidByColumn: string;
  splitStyle: SplitStyle;
  // --- delimited style (e.g. Dlužníček) ---
  participantsColumn: string | null;
  splitAmountsColumn: string | null;
  listDelimiter: string | null;
  // --- per-member-column style (e.g. some Splitwise exports) ---
  memberColumns: ImportMemberColumn[];
}

export interface ParsedSplit {
  /** Raw participant identity (name or email) as printed in the file. */
  token: string;
  amount: number;
}

export interface ParsedRow {
  description: string;
  amount: number | null;
  currencyCode: string | null;
  dateIso: string | null;
  categoryName: string | null;
  paidByToken: string | null;
  type: string | null;
  /** A debt settlement/transfer is imported as a settlement, not an expense. */
  kind: 'expense' | 'settlement';
  splitType: SplitType;
  splits: ParsedSplit[];
  warnings: string[];
}

export interface ImportMember {
  id: string;
  email: string | null;
  displayName: string;
}

export type ParticipantMatchType = 'email' | 'name' | 'fuzzy' | 'none';

export interface ParticipantMatch {
  memberId: string | null;
  matchType: ParticipantMatchType;
}

/** Absolute difference allowed between the sum of splits and the total. */
const SPLIT_SUM_TOLERANCE = 0.02;

const pad2 = (v: string | number): string => String(v).padStart(2, '0');

export function parseDelimitedList(value: string, delimiter: string): string[] {
  if (!value) return [];
  return String(value)
    .split(delimiter)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Parse a money string into a decimal number. Handles dot/comma decimal
 * separators and space/dot/comma thousands separators. Returns null when the
 * value is empty or not numeric.
 */
export function parseImportAmount(raw: string): number | null {
  if (raw == null) return null;
  const str = String(raw).trim();
  if (!str) return null;

  // Isolate the numeric portion so currency text/symbols in the same cell
  // (e.g. "1200 CZK", "$1,200.50", "1 234,56 Kč") don't break parsing.
  const match = str.match(/-?\d[\d\s.,]*/);
  if (!match) return null;
  let s = match[0].replace(/\s/g, '');

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  if (hasComma && hasDot) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      // comma is the decimal separator, dots are thousands
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // dot is the decimal separator, commas are thousands
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    const commaCount = (s.match(/,/g) || []).length;
    s = commaCount > 1 ? s.replace(/,/g, '') : s.replace(',', '.');
  }

  if (!/^-?\d*\.?\d+$/.test(s)) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  '€': 'EUR',
  '£': 'GBP',
  Kč: 'CZK',
  zł: 'PLN',
  '₹': 'INR',
  '₩': 'KRW',
  '¥': 'JPY',
  $: 'USD',
};

/**
 * Extract a currency from a free-text value: a 3-letter ISO code (e.g. "CZK")
 * or a known symbol (e.g. "$", "Kč"). Returns an uppercase ISO code or null.
 */
export function extractCurrencyCode(value: string): string | null {
  if (!value) return null;
  const s = String(value);
  const iso = s.match(/\b[A-Za-z]{3}\b/);
  if (iso) return iso[0].toUpperCase();
  for (const [symbol, code] of Object.entries(CURRENCY_SYMBOLS)) {
    if (s.includes(symbol)) return code;
  }
  return null;
}

/**
 * Parse a date string into an ISO date (YYYY-MM-DD) or datetime
 * (YYYY-MM-DDTHH:mm:ss). `hint` disambiguates slash/dot separated dates.
 * Returns null when unparseable.
 */
export function parseImportDate(raw: string, hint: DateFormatHint): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // ISO-like (unambiguous regardless of hint): "2025-09-06" or "2025-09-06 21:40:57"
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
  if (iso) {
    const [, y, mo, d, hh, mi, ss] = iso;
    const date = `${y}-${pad2(mo)}-${pad2(d)}`;
    return hh ? `${date}T${hh}:${mi}:${ss}` : date;
  }

  const parts = s.split(/[/.\-]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 3) {
    let y: string, mo: string, d: string;
    if (hint === 'DMY') [d, mo, y] = parts;
    else if (hint === 'MDY') [mo, d, y] = parts;
    else if (hint === 'YMD') [y, mo, d] = parts;
    else return null;

    if (y.length === 2) y = (parseInt(y, 10) <= 49 ? '20' : '19') + y;
    const yi = +y, moi = +mo, di = +d;
    if (yi > 1900 && moi >= 1 && moi <= 12 && di >= 1 && di <= 31) {
      return `${y}-${pad2(mo)}-${pad2(d)}`;
    }
  }

  return null;
}

export function normalizeName(name: string): string {
  return String(name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/**
 * Resolve a CSV participant token (email or display name) to a group member.
 * Tries exact email, then exact normalized name, then a conservative fuzzy
 * name match. Returns memberId null when nothing matches confidently.
 */
export function matchParticipant(token: string, members: ImportMember[]): ParticipantMatch {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return { memberId: null, matchType: 'none' };

  if (trimmed.includes('@')) {
    const lower = trimmed.toLowerCase();
    const byEmail = members.find((m) => m.email && m.email.toLowerCase() === lower);
    if (byEmail) return { memberId: byEmail.id, matchType: 'email' };
  }

  const norm = normalizeName(trimmed);
  const byName = members.find((m) => normalizeName(m.displayName) === norm);
  if (byName) return { memberId: byName.id, matchType: 'name' };

  const maxDist = Math.max(1, Math.floor(norm.length * 0.34));
  let best: { id: string; dist: number } | null = null;
  for (const m of members) {
    const dist = levenshtein(norm, normalizeName(m.displayName));
    if (dist <= maxDist && (best === null || dist < best.dist)) {
      best = { id: m.id, dist };
    }
  }
  if (best) return { memberId: best.id, matchType: 'fuzzy' };

  return { memberId: null, matchType: 'none' };
}

/** Values in a "type" column that mean a debt settlement rather than an expense. */
const SETTLEMENT_TYPES = ['transfer', 'payment', 'settlement', 'settle'];

export function isSettlementType(type: string | null): boolean {
  return !!type && SETTLEMENT_TYPES.includes(type.toLowerCase());
}

function cell(row: Record<string, string>, column: string | null): string {
  if (!column) return '';
  return (row[column] ?? '').trim();
}

/**
 * Apply a column mapping to one raw CSV row, producing a parsed expense draft
 * with any validation warnings. Tokens are NOT resolved to member ids here —
 * that happens in the orchestration layer where virtual members may be created.
 */
export function transformRow(row: Record<string, string>, mapping: ImportColumnMapping): ParsedRow {
  const warnings: string[] = [];

  const amount = parseImportAmount(cell(row, mapping.amountColumn));
  if (amount === null) warnings.push('unparseable_amount');

  let currencyCode: string | null;
  if (mapping.currencyColumn && mapping.currencyColumn === mapping.amountColumn) {
    // Amount and currency share one cell (e.g. "1200 CZK").
    currencyCode =
      extractCurrencyCode(cell(row, mapping.amountColumn)) ??
      (mapping.fallbackCurrency ? mapping.fallbackCurrency.toUpperCase() : null);
  } else {
    const currencyRaw = mapping.currencyColumn
      ? cell(row, mapping.currencyColumn)
      : (mapping.fallbackCurrency ?? '');
    currencyCode = currencyRaw ? currencyRaw.toUpperCase() : null;
  }
  if (!currencyCode) warnings.push('unknown_currency');

  const description = cell(row, mapping.descriptionColumn);
  const categoryName = cell(row, mapping.categoryColumn) || null;

  const dateRaw = cell(row, mapping.dateColumn);
  const dateIso = parseImportDate(dateRaw, mapping.dateFormat);
  if (dateRaw && !dateIso) warnings.push('unparseable_date');

  const type = mapping.typeColumn ? cell(row, mapping.typeColumn).toLowerCase() : null;
  let paidByToken = mapping.paidByColumn ? (cell(row, mapping.paidByColumn) || null) : null;

  let splits: ParsedSplit[] = [];

  if (mapping.splitStyle === 'delimited') {
    const delimiter = mapping.listDelimiter || ';';
    const participants = parseDelimitedList(cell(row, mapping.participantsColumn ?? ''), delimiter);
    const amountTokens = mapping.splitAmountsColumn
      ? parseDelimitedList(cell(row, mapping.splitAmountsColumn), delimiter)
      : [];

    if (amountTokens.length === participants.length && participants.length > 0) {
      splits = participants.map((token, i) => ({
        token,
        amount: parseImportAmount(amountTokens[i]) ?? 0,
      }));
    } else if (participants.length > 0) {
      // No usable per-participant amounts → fall back to an equal split.
      const each = amount !== null ? amount / participants.length : 0;
      splits = participants.map((token) => ({ token, amount: each }));
    }
  } else if (mapping.splitStyle === 'per_member') {
    // per_member: each column holds that participant's SIGNED net balance
    // (Splitwise convention): net = paid − owed. Reconstruct payer + exact shares.
    const nets = mapping.memberColumns
      .map((mc) => ({ token: mc.participant, net: parseImportAmount(cell(row, mc.columnName)) }))
      .filter((n): n is { token: string; net: number } => n.net !== null);

    if (nets.length > 0 && amount !== null) {
      // Payer: explicit column if present and matches a participant, else the
      // most-positive net (the person who fronted the money).
      let payer =
        paidByToken != null
          ? nets.find((n) => normalizeName(n.token) === normalizeName(paidByToken as string))
          : undefined;
      if (!payer) payer = nets.reduce((best, n) => (n.net > best.net ? n : best));
      paidByToken = payer.token;

      // owed_i = paid_i − net_i. Assuming a single payer who fronted the whole
      // cost: payer owes (cost − net), everyone else owes (−net).
      splits = nets.map(({ token, net }) => ({
        token,
        amount: token === payer!.token ? amount - net : -net,
      }));
    }
  }
  // splitStyle 'none': the file has no split info. Leave splits empty — the
  // orchestration layer (which knows the group members) splits equally among all.

  if (splits.length === 0 && mapping.splitStyle !== 'none') warnings.push('no_participants');

  if (amount !== null && splits.length > 0) {
    const sum = splits.reduce((acc, s) => acc + s.amount, 0);
    if (Math.abs(sum - amount) > SPLIT_SUM_TOLERANCE) warnings.push('split_sum_mismatch');
  }

  return {
    description,
    amount,
    currencyCode,
    dateIso,
    categoryName,
    paidByToken,
    type,
    kind: isSettlementType(type) ? 'settlement' : 'expense',
    splitType: 'exact',
    splits,
    warnings,
  };
}

/**
 * Stable key for duplicate detection across rows and against existing expenses.
 */
export function duplicateKey(input: {
  description: string;
  amount: number | null;
  dateIso: string | null;
  paidByToken: string | null;
  splitTokens?: string[];
}): string {
  const tokens = (input.splitTokens ?? []).map(normalizeName).sort().join(',');
  return [
    normalizeName(input.description),
    input.amount ?? '',
    input.dateIso ?? '',
    normalizeName(input.paidByToken ?? ''),
    tokens,
  ].join('|');
}
